import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export interface InlineFeeType {
    name: string;
    category: 'fee' | 'tax';
    default_amount: string;
    fee_trigger: string;
}

const TRIGGER_OPTIONS = [
    { value: 'manual', label: 'Manual' },
    { value: 'missed_payment', label: 'Missed Payment' },
    { value: 'minimum_only', label: 'Minimum Only' },
    { value: 'early_payoff', label: 'Early Payoff' },
];

interface FeeTypeManagerProps {
    feeTypes: InlineFeeType[];
    onChange: (feeTypes: InlineFeeType[]) => void;
}

export function FeeTypeManager({ feeTypes, onChange }: FeeTypeManagerProps) {
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState('');
    const [category, setCategory] = useState<'fee' | 'tax'>('fee');
    const [amount, setAmount] = useState('');
    const [trigger, setTrigger] = useState('manual');

    const reset = () => {
        setName('');
        setCategory('fee');
        setAmount('');
        setTrigger('manual');
        setAdding(false);
    };

    const add = () => {
        if (!name.trim() || !amount) return;
        onChange([...feeTypes, { name: name.trim(), category, default_amount: amount, fee_trigger: trigger }]);
        reset();
    };

    const remove = (index: number) => {
        onChange(feeTypes.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-3">
            {feeTypes.length > 0 && (
                <div className="space-y-2">
                    {feeTypes.map((ft, i) => (
                        <div key={i} className="flex items-center justify-between rounded-md border border-slate-700 bg-slate-800/50 px-3 py-2">
                            <div className="flex items-center gap-2">
                                <Badge variant={ft.category === 'tax' ? 'destructive' : 'secondary'} className="text-[10px]">
                                    {ft.category}
                                </Badge>
                                <span className="text-sm">{ft.name}</span>
                                <span className="text-sm text-slate-400">${parseFloat(ft.default_amount).toFixed(2)}</span>
                                {ft.fee_trigger !== 'manual' && (
                                    <span className="text-[10px] text-slate-500">
                                        ({TRIGGER_OPTIONS.find((t) => t.value === ft.fee_trigger)?.label})
                                    </span>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => remove(i)}
                                className="text-slate-500 hover:text-red-400 text-sm px-1"
                            >
                                &times;
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {adding ? (
                <div className="rounded-md border border-slate-600 bg-slate-800/80 p-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="mb-1 block text-xs text-slate-400">Name</label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Loser Tax"
                                className="h-8 text-sm"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs text-slate-400">Amount ($)</label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="50.00"
                                className="h-8 text-sm"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs text-slate-400">Category</label>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value as 'fee' | 'tax')}
                                className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-100"
                            >
                                <option value="fee">Fee</option>
                                <option value="tax">Tax</option>
                            </select>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs text-slate-400">Trigger</label>
                            <select
                                value={trigger}
                                onChange={(e) => setTrigger(e.target.value)}
                                className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-100"
                            >
                                {TRIGGER_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button type="button" size="sm" onClick={add} disabled={!name.trim() || !amount}>
                            Add
                        </Button>
                        <Button type="button" size="sm" variant="ghost" onClick={reset}>
                            Cancel
                        </Button>
                    </div>
                </div>
            ) : (
                <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)}>
                    + Add Fee / Tax Type
                </Button>
            )}

            {feeTypes.length === 0 && !adding && (
                <p className="text-xs text-slate-500">No fee types defined. You can add them now or later.</p>
            )}
        </div>
    );
}
